import { useCallback, useEffect, useRef } from "react";

interface UseAudioAnalysisProps {
  onFrequencyData?: (level: number) => void;
  onAudioDetected?: (detected: boolean) => void;
  audioThreshold?: number;
}

interface AudioAnalysisControls {
  startAnalysis: (stream: MediaStream) => void;
  stopAnalysis: () => void;
}

export function useAudioAnalysis({
  onFrequencyData,
  onAudioDetected,
  audioThreshold = 50,
}: UseAudioAnalysisProps = {}): AudioAnalysisControls {
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isAnalyzingRef = useRef(false);
  const audioDetectedRef = useRef(false);

  const stopAnalysis = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    isAnalyzingRef.current = false;
    audioDetectedRef.current = false;
  }, []);

  const startAnalysis = useCallback(
    (stream: MediaStream) => {
      try {
        stopAnalysis();

        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();

        analyser.fftSize = 256;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        isAnalyzingRef.current = true;

        const draw = () => {
          if (!analyserRef.current || !isAnalyzingRef.current) {
            return;
          }

          analyserRef.current.getByteFrequencyData(dataArray);
          const level = Math.max(...dataArray);

          onFrequencyData?.(level);

          if (onAudioDetected && level > audioThreshold && !audioDetectedRef.current) {
            audioDetectedRef.current = true;
            onAudioDetected(true);
          }

          animationFrameRef.current = requestAnimationFrame(draw);
        };

        draw();
      } catch (error) {
        console.error("Failed to start audio analysis:", error);
        stopAnalysis();
      }
    },
    [onFrequencyData, onAudioDetected, audioThreshold, stopAnalysis],
  );

  useEffect(() => {
    return () => {
      stopAnalysis();
    };
  }, [stopAnalysis]);

  return {
    startAnalysis,
    stopAnalysis,
  };
}
